/**
 * NotificationsDrawer — role-aware alerts.
 *
 * Supports two notification shapes:
 *  1. Legacy (admin/captain ephemeral): { id, type, icon, title, body, action, priority }
 *  2. Player (DB-backed): same shape after mapping in BottomNav, plus is_read, created_at
 *
 * Props:
 *  - notifications    : array of notification objects
 *  - isAdmin/isCaptain/isPlayer : role booleans
 *  - onMarkRead(id)   : mark a single notification as read
 *  - onMarkAllRead()  : mark all as read
 *  - onDelete(id)     : delete a notification
 *
 * Theme-aware: uses Tailwind dark: variants throughout.
 */
import React, { useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Bell,
  BellOff,
  ClipboardList,
  Users,
  Calendar,
  ShieldAlert,
  CheckCircle2,
  ChevronRight,
  Zap,
  Trophy,
  Camera,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DrawerShell from "./DrawerShell";

// ── Icon map ──────────────────────────────────────────────────────────────────
// Keys must match the strings returned by mapNotifTypeToIcon() in BottomNav.jsx.
const ICON_MAP = {
  clipboard: ClipboardList,
  "clipboard-list": ClipboardList,
  users: Users,
  calendar: Calendar,
  basketball: Trophy,
  shield: ShieldAlert,
  "shield-alert": ShieldAlert,
  zap: Zap,
  camera: Camera,
};

// ── Role badge ────────────────────────────────────────────────────────────────
const RoleBadge = ({ type }) => {
  const config = {
    admin: {
      label: "Admin",
      cls: "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30",
    },
    captain: {
      label: "Captain",
      cls: "bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/30",
    },
    player: {
      label: "Player",
      cls: "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/30",
    },
  }[type] || {
    label: type,
    cls: "bg-gray-100 dark:bg-gray-500/20 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-500/30",
  };

  return (
    <span
      className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full border ${config.cls}`}
    >
      {config.label}
    </span>
  );
};

// ── Priority dot ──────────────────────────────────────────────────────────────
const PriorityDot = ({ priority }) => {
  if (priority !== 1) return null;
  return (
    <span className="relative flex h-2 w-2 flex-shrink-0">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-60" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
    </span>
  );
};

// ── Unread dot ────────────────────────────────────────────────────────────────
const UnreadDot = ({ isRead }) => {
  if (isRead) return null;
  return (
    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-1.5" />
  );
};

// ── Notification card ─────────────────────────────────────────────────────────
const NotifCard = ({ notif, index, onAction, onMarkRead, onDelete }) => {
  const IconComp = ICON_MAP[notif.icon] || Bell;
  const isRead = notif.is_read ?? false;

  const iconBg =
    {
      admin: "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400",
      captain:
        "bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400",
      player:
        "bg-orange-100 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400",
    }[notif.type] ||
    "bg-gray-100 dark:bg-gray-500/15 text-gray-500 dark:text-gray-400";

  const handleClick = useCallback(() => {
    // Mark as read when tapped
    if (!isRead && onMarkRead) {
      onMarkRead(notif.id);
    }
    if (notif.action) {
      onAction(notif.action);
    }
  }, [isRead, notif.id, notif.action, onMarkRead, onAction]);

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      if (onDelete) onDelete(notif.id);
    },
    [notif.id, onDelete],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{
        delay: index * 0.06,
        type: "spring",
        stiffness: 380,
        damping: 32,
      }}
      className={`flex items-start gap-3.5 w-full text-left px-5 py-4 group relative
        transition-colors
        ${
          isRead
            ? "opacity-60 hover:opacity-100"
            : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
        }
        active:bg-gray-100 dark:active:bg-white/[0.07]`}
    >
      {/* Unread indicator */}
      <UnreadDot isRead={isRead} />

      {/* Clickable area */}
      <button
        onClick={handleClick}
        className="flex items-start gap-3.5 flex-1 min-w-0 text-left"
      >
        {/* Icon */}
        <div
          className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${iconBg}`}
        >
          <IconComp size={18} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p
              className={`text-sm font-bold truncate ${
                isRead
                  ? "text-gray-500 dark:text-gray-400"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              {notif.title}
            </p>
            <PriorityDot priority={notif.priority} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
            {notif.body}
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <RoleBadge type={notif.type} />
            {notif.created_at && (
              <span className="text-[10px] text-gray-400 dark:text-gray-600">
                {formatRelativeTime(notif.created_at)}
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        {notif.action && (
          <ChevronRight
            size={15}
            className="text-gray-300 dark:text-gray-600 flex-shrink-0 mt-1 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors"
          />
        )}
      </button>

      {/* Delete button — appears on hover */}
      {onDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-3 right-3 w-6 h-6 rounded-full
            bg-gray-100 dark:bg-gray-800 hover:bg-red-100 dark:hover:bg-red-900/40
            flex items-center justify-center
            opacity-0 group-hover:opacity-100 transition-opacity"
          title="Dismiss"
        >
          <X size={11} className="text-gray-400 hover:text-red-500" />
        </button>
      )}
    </motion.div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.1 }}
    className="flex flex-col items-center justify-center py-16 px-5 text-center"
  >
    <div
      className="w-16 h-16 rounded-2xl
                    bg-gray-100 dark:bg-gray-900
                    border border-gray-200 dark:border-white/[0.07]
                    flex items-center justify-center mb-4"
    >
      <BellOff size={28} className="text-gray-300 dark:text-gray-700" />
    </div>
    <p className="text-gray-600 dark:text-gray-400 text-sm font-semibold">
      All caught up!
    </p>
    <p className="text-gray-400 dark:text-gray-600 text-xs mt-1 max-w-[200px]">
      No pending actions or alerts right now.
    </p>
  </motion.div>
);

// ── Quick action buttons (role-specific) ──────────────────────────────────────
const QuickActions = ({ isAdmin, isCaptain, isPlayer, onNav }) => {
  const actions = [];

  if (isAdmin) {
    actions.push(
      {
        label: "Score Entry",
        path: "/scorer",
        icon: Zap,
        color:
          "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20",
      },
      {
        label: "Admin Panel",
        path: "/admin",
        icon: ShieldAlert,
        color:
          "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20",
      },
    );
  }
  if (isCaptain) {
    actions.push({
      label: "My Team",
      path: "/captain",
      icon: Users,
      color:
        "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20",
    });
  }
  if (isPlayer || isCaptain) {
    actions.push({
      label: "Schedule",
      path: "/schedule",
      icon: Calendar,
      color:
        "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20",
    });
  }

  if (!actions.length) return null;

  return (
    <div className="mx-5 mt-4 mb-2">
      <p className="text-[10px] font-black tracking-[0.18em] uppercase text-gray-400 dark:text-gray-500 mb-2.5">
        Quick Actions
      </p>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((a, i) => {
          const Icon = a.icon;
          return (
            <motion.button
              key={a.path}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => onNav(a.path)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all active:scale-95 ${a.color}`}
            >
              <Icon size={14} />
              {a.label}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

// ── Relative time formatter ───────────────────────────────────────────────────
function formatRelativeTime(isoString) {
  if (!isoString) return "";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ── Main component ────────────────────────────────────────────────────────────
const NotificationsDrawer = ({
  open,
  onClose,
  notifications,
  isAdmin,
  isCaptain,
  isPlayer,
  onMarkRead,
  onMarkAllRead,
  onDelete,
}) => {
  const navigate = useNavigate();

  const handleAction = (path) => {
    if (path) {
      onClose();
      navigate(path);
    }
  };

  const handleMarkAllRead = () => {
    if (onMarkAllRead) onMarkAllRead();
  };

  const unreadCount = notifications.filter((n) => !(n.is_read ?? false)).length;
  const urgentCount = notifications.filter((n) => n.priority === 1).length;

  // Sort: unread first, then by priority, then by created_at desc
  const sorted = [...notifications].sort((a, b) => {
    const aRead = a.is_read ?? false;
    const bRead = b.is_read ?? false;
    if (aRead !== bRead) return aRead ? 1 : -1;
    if (a.priority !== b.priority) return (a.priority ?? 2) - (b.priority ?? 2);
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Notifications"
      subtitle={
        notifications.length > 0
          ? `${notifications.length} alert${notifications.length !== 1 ? "s" : ""}${unreadCount > 0 ? ` · ${unreadCount} unread` : ""}${urgentCount > 0 ? ` · ${urgentCount} urgent` : ""}`
          : "No new alerts"
      }
    >
      <div className="pb-4">
        {/* Quick actions always visible */}
        <QuickActions
          isAdmin={isAdmin}
          isCaptain={isCaptain}
          isPlayer={isPlayer}
          onNav={handleAction}
        />

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="mx-5 mt-4 mb-1 flex items-center justify-between">
              <p className="text-[10px] font-black tracking-[0.18em] uppercase text-gray-400 dark:text-gray-500">
                Alerts
              </p>
              {unreadCount > 0 && onMarkAllRead && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-[10px] font-semibold text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              <AnimatePresence>
                {sorted.map((notif, i) => (
                  <NotifCard
                    key={notif.id}
                    notif={notif}
                    index={i}
                    onAction={handleAction}
                    onMarkRead={onMarkRead}
                    onDelete={onDelete}
                  />
                ))}
              </AnimatePresence>
            </div>
          </>
        )}

        {/* Dismiss all / close */}
        {notifications.length > 0 && (
          <div className="mx-5 mt-4">
            <button
              onClick={onClose}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                         bg-gray-100 dark:bg-white/[0.04]
                         border border-gray-200 dark:border-white/[0.07]
                         hover:bg-gray-200 dark:hover:bg-white/[0.08]
                         transition-colors text-sm font-semibold
                         text-gray-500 dark:text-gray-400
                         hover:text-gray-800 dark:hover:text-white"
            >
              <CheckCircle2 size={14} />
              Close
            </button>
          </div>
        )}
      </div>
    </DrawerShell>
  );
};

export default NotificationsDrawer;
